import { useEffect, useCallback, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { STORAGE_KEYS } from '../config';
import type { QuestionnaireData } from '../types';
import { DEFAULT_FORM_DATA } from '../types';
import { debounce } from '../lib/utils';

interface UseFormPersistenceOptions {
  form: UseFormReturn<QuestionnaireData>;
  debounceMs?: number;
}

export function useFormPersistence({
  form,
  debounceMs = 1000,
}: UseFormPersistenceOptions) {
  const { watch, reset } = form;
  const isInitialized = useRef(false);

  // Load saved data on mount
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    try {
      const savedData = localStorage.getItem(STORAGE_KEYS.formData);
      if (savedData) {
        const parsed = JSON.parse(savedData) as QuestionnaireData;
        reset(parsed);
      }
    } catch (error) {
      console.error('Failed to load saved form data:', error);
    }
  }, [reset]);

  // Create debounced save function
  const saveToStorage = useCallback(
    debounce((data: QuestionnaireData) => {
      try {
        localStorage.setItem(STORAGE_KEYS.formData, JSON.stringify(data));
      } catch (error) {
        console.error('Failed to save form data:', error);
      }
    }, debounceMs),
    [debounceMs]
  );

  // Watch all form values and save on change
  useEffect(() => {
    const subscription = watch((data) => {
      saveToStorage(data as QuestionnaireData);
    });

    return () => subscription.unsubscribe();
  }, [watch, saveToStorage]);

  // Clear saved data
  const clearSavedData = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.formData);
      localStorage.removeItem(STORAGE_KEYS.currentStep);
      reset(DEFAULT_FORM_DATA);
    } catch (error) {
      console.error('Failed to clear saved data:', error);
    }
  }, [reset]);

  // Get saved step
  const getSavedStep = useCallback((): number => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.currentStep);
      return saved ? parseInt(saved, 10) : 1;
    } catch {
      return 1;
    }
  }, []);

  // Save current step
  const saveStep = useCallback((step: number) => {
    try {
      localStorage.setItem(STORAGE_KEYS.currentStep, String(step));
    } catch (error) {
      console.error('Failed to save step:', error);
    }
  }, []);

  return {
    clearSavedData,
    getSavedStep,
    saveStep,
  };
}
